import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { BackButton } from "../../components/common/BackButton";
import {
  useShopCartStore,
} from "../../stores/shopCart.store";
import { NotFound } from "../404";
import { clampPurchaseQuantity, ShopProductDetail } from "./catalogue";
import { fetchShopProductById } from "./shop.service";
import {
  ActionButton,
  AvailabilityBadge,
  Container,
  ErrorMessage,
  StateMessage,
} from "./shop.styles";
import { useShopAccess } from "./useShopAccess";

// The detail page is narrower than the catalogue grid, so it gets its own
// centred column instead of the full-width shop Container.
const DetailContainer = styled(Container)`
  max-width: 1188px;
  padding-top: 20px;

  @media (min-width: 768px) {
    padding-top: 28px;
  }
`;

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (min-width: 900px) {
    align-items: flex-start;
    flex-direction: row;
    gap: 64px;
    justify-content: center;
  }
`;

const Gallery = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;

  @media (min-width: 900px) {
    flex: 1 1 auto;
    max-width: 744px;
  }
`;

const MainImage = styled.img<{ $isSoldOut: boolean }>`
  aspect-ratio: 347 / 260;
  border: 0.5px solid #000;
  display: block;
  object-fit: cover;
  width: 100%;
  filter: ${(props) => (props.$isSoldOut ? "grayscale(0.35)" : "none")};

  @media (min-width: 900px) {
    aspect-ratio: 744 / 520;
  }
`;

const GalleryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 11px;

  @media (min-width: 900px) {
    gap: 16px;
  }
`;

const GalleryThumb = styled.button<{ $isSelected: boolean }>`
  background: none;
  border: ${(props) =>
    props.$isSelected ? "2px solid #000" : "0.5px solid #999"};
  cursor: pointer;
  display: block;
  height: 80px;
  padding: 0;
  width: 80px;

  @media (min-width: 900px) {
    height: 120px;
    width: 120px;
  }
`;

const GalleryImage = styled.img`
  display: block;
  height: 100%;
  object-fit: cover;
  width: 100%;
`;

const Purchase = styled.div`
  display: flex;
  flex-direction: column;

  @media (min-width: 900px) {
    flex: 0 0 348px;
    width: 348px;
  }
`;

const ProductName = styled.h1`
  color: #000;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  margin: 0 0 14px;
  text-transform: uppercase;
`;

const PriceRow = styled.div`
  align-items: baseline;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const Price = styled.span`
  color: #000;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
`;

const Description = styled.p`
  color: #000;
  font-size: 14px;
  line-height: 1.35;
  margin: 0 0 28px;
  white-space: pre-line;
`;

const SectionTitle = styled.span`
  color: #000;
  display: block;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  margin-bottom: 12px;
`;

const VariantList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 26px;
`;

const VariantLabel = styled.span`
  color: #000;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  text-transform: uppercase;
`;

const VariantAvailability = styled.span`
  color: #000;
  font-size: 12px;
  font-weight: 300;
  line-height: 1.3;
  text-transform: uppercase;
`;

// Read-only for visitors and non members: only sold out variants are dimmed,
// so everyone can still read the availability of the others.
const VariantOption = styled.button<{
  $isSelected: boolean;
  $isSoldOut: boolean;
}>`
  align-items: center;
  background: ${(props) => (props.$isSelected ? "#000" : "transparent")};
  border: 1px solid #000;
  color: ${(props) => (props.$isSelected ? "#fff" : "#000")};
  cursor: pointer;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-height: 43px;
  padding: 8px 12px;
  opacity: ${(props) => (props.$isSoldOut ? 0.38 : 1)};
  text-align: left;
  width: 100%;

  /* The label and availability spans carry their own colour: let the selected
     row repaint them so they stay readable on the black background. */
  span {
    color: inherit;
  }

  &:disabled {
    cursor: default;
  }
`;

const QuantityRow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  margin-bottom: 26px;
`;

const QuantityButton = styled.button`
  background: none;
  border: 1px solid #000;
  color: #000;
  cursor: pointer;
  font-size: 18px;
  font-weight: 600;
  height: 36px;
  line-height: 1;
  width: 36px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.38;
  }
`;

const QuantityValue = styled.span`
  color: #000;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  min-width: 24px;
  text-align: center;
`;

const SoldOutNotice = styled.p`
  color: #000;
  font-size: 12px;
  font-weight: 300;
  line-height: 1.3;
  margin: 0 0 26px;
  text-transform: uppercase;
`;

const GoToCartButton = styled(ActionButton)`
  margin-top: 10px;
`;

const ErrorFeedbackMessage = styled.p`
  background: #ffefef;
  border: 1px solid #c40000;
  color: #c40000;
  font-size: 13px;
  line-height: 1.35;
  margin: 16px 0 0;
  padding: 10px 12px;
`;

const SuccessToast = styled.div`
  background: #000;
  bottom: 24px;
  color: #fff;
  font-size: 13px;
  left: 50%;
  line-height: 1.35;
  max-width: calc(100vw - 32px);
  padding: 12px 16px;
  position: fixed;
  transform: translateX(-50%);
  z-index: 200;

  @media (min-width: 768px) {
    bottom: 32px;
  }
`;

// Supabase rejects a malformed uuid with an error: treat those ids as not found.
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const ProductDetail = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<ShopProductDetail | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    null,
  );
  const [quantity, setQuantity] = useState(1);
  const [errorFeedback, setErrorFeedback] = useState<string | null>(null);
  const [successToast, setSuccessToast] = useState<string | null>(null);
  const access = useShopAccess();
  const items = useShopCartStore((state) => state.items);
  const setCartItem = useShopCartStore((state) => state.setCartItem);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);

      if (!productId || !UUID_PATTERN.test(productId)) {
        setProduct(null);
        setSelectedImageIndex(0);
        setSelectedVariantId(null);
        setQuantity(1);
        setErrorFeedback(null);
        setSuccessToast(null);
        setLoading(false);
        return;
      }

      try {
        const detail = await fetchShopProductById(productId);

        setProduct(detail);
        setSelectedImageIndex(0);
        setSelectedVariantId(null);
        setQuantity(1);
        setErrorFeedback(null);
        setSuccessToast(null);
      } catch (err) {
        console.error("Error fetching shop product:", err);
        setError("Errore nel caricamento del prodotto");
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId]);

  useEffect(() => {
    if (!successToast) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setSuccessToast(null);
    }, 2200);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [successToast]);

  if (loading) {
    return (
      <Container>
        <StateMessage>Caricamento...</StateMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
      </Container>
    );
  }

  if (!product) {
    return <NotFound />;
  }

  const selectedVariant =
    product.variants.find((variant) => variant.id === selectedVariantId) ??
    null;
  const maxQuantity = selectedVariant?.availableQuantity ?? 0;

  const selectVariant = (variantId: string, availableQuantity: number) => {
    setSelectedVariantId(variantId);
    setQuantity(clampPurchaseQuantity(quantity, availableQuantity));
    setErrorFeedback(null);
  };

  const handleAddToCart = async () => {
    if (!selectedVariant) {
      setErrorFeedback(
        "Seleziona una variante disponibile prima di aggiungere al carrello.",
      );
      return;
    }

    if (quantity <= 0) {
      setErrorFeedback(
        "Seleziona una quantità valida prima di aggiungere al carrello.",
      );
      return;
    }

    const existingQuantity = items.find((item) => {
      return (
        item.productId === product.id && item.variantId === selectedVariant.id
      );
    })?.quantity ?? 0;

    try {
      await setCartItem(
        product.id,
        selectedVariant.id,
        existingQuantity + quantity,
      );
    } catch (err) {
      console.error("Error adding the product to the shop cart:", err);
      setErrorFeedback(
        "Non è stato possibile aggiungere l'articolo al carrello. Riprova.",
      );
      return;
    }

    setErrorFeedback(null);
    setSuccessToast("Articolo aggiunto al carrello");
  };

  const canPurchase = access === "member" && !product.isSoldOut;
  const images = [product.mainImageUrl, ...product.galleryImageUrls];
  const displayedImageUrl = images[selectedImageIndex] ?? product.mainImageUrl;

  return (
    <DetailContainer>
      <BackButton onClick={() => navigate("/shop")} />
      <Layout>
        <Gallery>
          <MainImage
            $isSoldOut={product.isSoldOut}
            alt={product.name}
            src={displayedImageUrl}
          />
          {images.length > 1 && (
            <GalleryRow>
              {images.map((imageUrl, index) => (
                <GalleryThumb
                  key={`${imageUrl}-${index}`}
                  $isSelected={index === selectedImageIndex}
                  aria-label={`Mostra immagine ${index + 1} di ${product.name}`}
                  onClick={() => setSelectedImageIndex(index)}
                  type="button"
                >
                  <GalleryImage
                    alt={`${product.name} - immagine ${index + 1}`}
                    src={imageUrl}
                  />
                </GalleryThumb>
              ))}
            </GalleryRow>
          )}
        </Gallery>
        <Purchase>
          <ProductName>{product.name}</ProductName>
          <PriceRow>
            <Price>{product.priceLabel}</Price>
            <AvailabilityBadge $isSoldOut={product.isSoldOut}>
              {product.availabilityLabel}
            </AvailabilityBadge>
          </PriceRow>
          <Description>{product.description}</Description>
          <SectionTitle>Varianti</SectionTitle>
          <VariantList>
            {product.variants.map((variant) => (
              <VariantOption
                key={variant.id}
                $isSelected={variant.id === selectedVariantId}
                $isSoldOut={variant.isSoldOut}
                aria-pressed={variant.id === selectedVariantId}
                disabled={!canPurchase || variant.isSoldOut}
                onClick={() =>
                  selectVariant(variant.id, variant.availableQuantity)
                }
                type="button"
              >
                <VariantLabel>{variant.label}</VariantLabel>
                <VariantAvailability>
                  {variant.availabilityLabel}
                </VariantAvailability>
              </VariantOption>
            ))}
          </VariantList>
          {canPurchase && selectedVariant && (
            <>
              <SectionTitle>Quantità</SectionTitle>
              <QuantityRow>
                <QuantityButton
                  aria-label="Diminuisci quantità"
                  disabled={quantity <= 1}
                  onClick={() => {
                    setQuantity(clampPurchaseQuantity(quantity - 1, maxQuantity));
                    setErrorFeedback(null);
                  }}
                  type="button"
                >
                  -
                </QuantityButton>
                <QuantityValue>{quantity}</QuantityValue>
                <QuantityButton
                  aria-label="Aumenta quantità"
                  disabled={quantity >= maxQuantity}
                  onClick={() => {
                    setQuantity(clampPurchaseQuantity(quantity + 1, maxQuantity));
                    setErrorFeedback(null);
                  }}
                  type="button"
                >
                  +
                </QuantityButton>
              </QuantityRow>
            </>
          )}
          {canPurchase && (
            <>
              <ActionButton fullWidth onClick={() => void handleAddToCart()}>
                Aggiungi al carrello
              </ActionButton>
              <GoToCartButton
                fullWidth
                onClick={() => navigate("/shop/cart")}
                variant="outline"
              >
                Vai al carrello
              </GoToCartButton>
              {errorFeedback && (
                <ErrorFeedbackMessage aria-live="polite">
                  {errorFeedback}
                </ErrorFeedbackMessage>
              )}
            </>
          )}
          {product.isSoldOut && (
            <>
              <SoldOutNotice>
                Prodotto esaurito: al momento non è acquistabile.
              </SoldOutNotice>
              <ActionButton onClick={() => navigate("/shop")}>
                Torna allo shop
              </ActionButton>
            </>
          )}
        </Purchase>
      </Layout>
      {successToast && <SuccessToast>{successToast}</SuccessToast>}
    </DetailContainer>
  );
};
